import { API_BASE_URL, ADMIN_API_BASE_URL } from '../config';

const getAuthCreds = () => {
  return {
    auth_key: localStorage.getItem('authKey') || 'admin',
    user_id: localStorage.getItem('userId') || 'admin'
  };
};

export const authService = {
  async getCollectionProducts(collectionHandle) {
    try {
      const { auth_key, user_id } = getAuthCreds();
      const response = await fetch(`${API_BASE_URL}/brands/collections/products`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ auth_key, user_id, collection_handle: collectionHandle }),
      });
      const data = await response.json();
      if (!response.ok) {
        return { status: 200, data: [] };
      }
      return data;
    } catch (error) {
      console.warn('getCollectionProducts error:', error);
      return { status: 200, data: [] };
    }
  },

  async getCollectionSales(handle, startDate, endDate) {
    try {
      const { auth_key, user_id } = getAuthCreds();
      const response = await fetch(`${API_BASE_URL}/brands/collections/sales`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          auth_key,
          user_id,
          collection_handle: handle,
          start_date: startDate,
          end_date: endDate
        }),
      });
      const data = await response.json();
      if (!response.ok) {
        return { status: 200, data: [] };
      }
      return data;
    } catch (error) {
      console.warn('getCollectionSales error:', error);
      return { status: 200, data: [] };
    }
  },

  getOrders: async (params = {}) => {
    try {
      const { auth_key, user_id } = getAuthCreds();
      const queryParams = new URLSearchParams();
      if (params.page) queryParams.append('page', params.page);
      if (params.limit) queryParams.append('limit', params.limit);
      if (params.status) queryParams.append('status', params.status);
      if (params.fromDate) queryParams.append('from_date', params.fromDate);
      if (params.toDate) queryParams.append('to_date', params.toDate);

      const queryString = queryParams.toString();
      const url = `${API_BASE_URL}/brands/orders${queryString ? `?${queryString}` : ''}`;

      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ auth_key, user_id }),
      });
      if (response.ok) {
        return await response.json();
      }
      return { status: 200, data: [], pagination: { current_page: 1, total_pages: 1, total_items: 0 } };
    } catch (error) {
      console.warn('getOrders error:', error);
      return { status: 200, data: [], pagination: { current_page: 1, total_pages: 1, total_items: 0 } };
    }
  },

  async getAllSales(startDate, endDate) {
    try {
      const { auth_key, user_id } = getAuthCreds();
      const response = await fetch(`${API_BASE_URL}/brands/collections/sales`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          auth_key,
          user_id,
          collection_handle: 'all',
          start_date: startDate,
          end_date: endDate
        }),
      });
      const data = await response.json();
      if (!response.ok) {
        return { status: 200, data: [] };
      }
      return data;
    } catch (error) {
      console.warn('getAllSales error:', error);
      return { status: 200, data: [] };
    }
  },

  async checkAdminStatus() {
    // In admin.polopan.com, all authenticated users are admins
    return true;
  },

  initiateGoogleLogin(nextPath = '/dashboard') {
    const nextUrl = `${window.location.origin}/login`;
    window.location.href = `${ADMIN_API_BASE_URL}/auth/google/login?next=${encodeURIComponent(nextUrl)}`;
  },

  processGoogleAuthResponse(authParams) {
    if (authParams.token && authParams.auth_key && authParams.user_id) {
      localStorage.setItem('isLoggedIn', 'true');
      localStorage.setItem('authToken', authParams.token);
      localStorage.setItem('authKey', authParams.auth_key);
      localStorage.setItem('userId', authParams.user_id);
      localStorage.setItem('isAdmin', 'true');
      localStorage.setItem('onboardingComplete', 'true');
      return true;
    }
    return false;
  },

  async acceptTerms(version) {
    try {
      const { auth_key, user_id } = getAuthCreds();
      const response = await fetch(`${API_BASE_URL}/brands/accounts/accept-terms`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ auth_key, user_id, version }),
      });
      return await response.json();
    } catch (error) {
      return { status: 200 };
    }
  },

  async getUserProfile(targetUserId) {
    // 1. Try checking /auth/me for admin session
    try {
      const authToken = localStorage.getItem('authToken') || localStorage.getItem('authKey') || '';
      const meRes = await fetch(`${ADMIN_API_BASE_URL}/auth/me`, {
        credentials: 'include',
        headers: {
          'Accept': 'application/json',
          ...(authToken ? { 'Authorization': `Bearer ${authToken}` } : {}),
          'X-Auth-Key': localStorage.getItem('authKey') || '',
          'X-User-Id': localStorage.getItem('userId') || ''
        }
      });

      if (meRes.ok) {
        const meData = await meRes.json();
        const email = meData.email || meData.user?.email || '';
        const name = meData.name || meData.user?.name || 'Admin';
        const userId = meData.id || meData.user_id || meData.user?.id || 'admin';
        const authKey = meData.auth_key || meData.authKey || 'admin';
        if (email) localStorage.setItem('userEmail', email);
        if (name) localStorage.setItem('userName', name);
        localStorage.setItem('userId', userId);
        localStorage.setItem('authKey', authKey);
        localStorage.setItem('isLoggedIn', 'true');
        localStorage.setItem('isAdmin', 'true');
        localStorage.setItem('onboardingComplete', 'true');
        return { status: 200, data: { email, name, admin: true } };
      }
    } catch (e) {
      console.warn('getMe check failed:', e);
    }

    // 2. Fallback to /brands/accounts/profile
    try {
      const { auth_key, user_id } = getAuthCreds();
      const payload = { auth_key, user_id };
      if (targetUserId) payload.target_user_id = targetUserId;

      const response = await fetch(`${API_BASE_URL}/brands/accounts/profile`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await response.json();
      if (data && data.data) {
        if (data.data.email) localStorage.setItem('userEmail', data.data.email);
        if (data.data.name) localStorage.setItem('userName', data.data.name);
      }
      return data;
    } catch (error) {
      return {
        status: 200,
        data: {
          email: localStorage.getItem('userEmail') || '',
          name: localStorage.getItem('userName') || 'Admin',
          admin: true
        }
      };
    }
  },

  async updateUserProfile(profileData, targetUserId) {
    try {
      const { auth_key, user_id } = getAuthCreds();
      const payload = { auth_key, user_id, ...profileData };
      if (targetUserId) payload.target_user_id = targetUserId;

      const response = await fetch(`${API_BASE_URL}/brands/accounts/profile/update`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      return await response.json();
    } catch (error) {
      return { status: 200 };
    }
  },

  async getWarehouses() {
    try {
      const { auth_key, user_id } = getAuthCreds();
      const response = await fetch(`${API_BASE_URL}/brands/warehouses/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ auth_key, user_id })
      });
      const data = await response.json();
      return data.data || [];
    } catch (error) {
      return [];
    }
  },

  async getInventory() {
    try {
      const { auth_key, user_id } = getAuthCreds();
      const response = await fetch(`${API_BASE_URL}/brands/inventory`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ auth_key, user_id })
      });
      const data = await response.json();
      return data.data || [];
    } catch (error) {
      return [];
    }
  },

  async getReturns() {
    try {
      const { auth_key, user_id } = getAuthCreds();
      const response = await fetch(`${API_BASE_URL}/brands/returns`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ auth_key, user_id })
      });
      const data = await response.json();
      return data.data || [];
    } catch (error) {
      return [];
    }
  },

  async getTickets() {
    try {
      const { auth_key, user_id } = getAuthCreds();
      const response = await fetch(`${API_BASE_URL}/brands/tickets`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ auth_key, user_id })
      });
      const data = await response.json();
      return data.data || [];
    } catch (error) {
      return [];
    }
  },

  async getLedger() {
    try {
      const { auth_key, user_id } = getAuthCreds();
      const response = await fetch(`${API_BASE_URL}/brands/ledger`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ auth_key, user_id })
      });
      const data = await response.json();
      return data.data || [];
    } catch (error) {
      return [];
    }
  },

  async listAccounts() {
    try {
      const { auth_key, user_id } = getAuthCreds();
      const response = await fetch(`${API_BASE_URL}/brands/accounts/list`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ auth_key, user_id })
      });
      return await response.json();
    } catch (error) {
      return { status: 200, data: { accounts: [] } };
    }
  },

  async logout() {
    try {
      await fetch(`${ADMIN_API_BASE_URL}/auth/logout`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Accept': 'application/json' }
      });
    } catch (e) {
      console.warn('Backend logout request failed:', e);
    } finally {
      localStorage.clear();
      window.location.href = '/login';
    }
  }
};
